//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';

import {
  connectYouTube,
  disconnectYouTube,
  getYouTubeAuthorizationUrl,
  getYouTubeConnectionStatus,
} from '@apis/youtube-settings/youtubeSettingsApi';
import { IOAuthYouTubeRequest } from '@apis/youtube-settings/youtubeSettingsApiModel';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import { navigateToState } from '@react-bridge/wrappers/navigation';

// TODO: PD-264381: Remove isEnabled after cleanup youtube_automatic_posting feature flag
export function useGetYouTubeConnectionStatusQuery(isEnabled: boolean) {
  return useQuery({
    queryKey: [
      QueryKeyConstants.AccountSettings,
      QueryKeyConstants.AccountSettingsYouTube,
      QueryKeyConstants.AccountSettingsGetYouTubeConnectionStatus,
    ],
    queryFn: async ({ signal }) => {
      return getYouTubeConnectionStatus({ signal });
    },
    enabled: isEnabled,
    staleTime: Infinity,
  });
}

export function useGetYouTubeAuthorizationUrlQuery() {
  return useQuery({
    queryKey: [
      QueryKeyConstants.AccountSettings,
      QueryKeyConstants.AccountSettingsYouTube,
      QueryKeyConstants.AccountSettingsGetYouTubeAuthorizationUrl,
    ],
    queryFn: async ({ signal }) => {
      return getYouTubeAuthorizationUrl({ signal });
    },
    staleTime: Infinity,
  });
}

export const useConnectYouTubeMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: IOAuthYouTubeRequest) => {
      return connectYouTube(request);
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [
          QueryKeyConstants.AccountSettings,
          QueryKeyConstants.AccountSettingsYouTube,
        ],
      });
    },
    onSuccess: () => {
      navigateToState('account-settings.social', {
        ['oauth-network']: 'youtube',
      });
    },
    onError: () => {
      navigateToState('account-settings.social', {
        ['oauth-network']: 'youtube',
        ['oauth-error']: '1',
      });
    },
  });
};

export const useDisconnectYouTubeMutation = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      return disconnectYouTube();
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [
          QueryKeyConstants.AccountSettings,
          QueryKeyConstants.AccountSettingsYouTube,
        ],
      });
    },
    onSuccess: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'YouTube Disconnected',
          message: 'YouTube was successfully disconnected.',
          type: ToastType.SUCCESS,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
    onError: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'YouTube Disconnect Failed',
          message:
            'An unexpected error occurred. Please try again or contact Bazaarvoice Support.',
          type: ToastType.ERROR,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
  });
};

export const useReconnectYouTubeMutation = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (oauthEndpoint: string) => {
      await disconnectYouTube();
      window.location.href = oauthEndpoint;
    },
    onError: () => {
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Reconnecting YouTube Account Failed',
          message:
            'An unexpected error occurred. Please try again or contact Bazaarvoice Support.',
          type: ToastType.ERROR,
          options: { toastTemplate: ALTERNATE_TOASTS.NEW_TOAST },
        }),
      );
    },
    onSettled: async () => {
      await queryClient.invalidateQueries([
        QueryKeyConstants.AccountSettings,
        QueryKeyConstants.AccountSettingsYouTube,
      ]);
    },
  });
};
